import { useState } from 'react';
import { Button, Dropdown, type MenuProps } from 'antd';
import {
  EnvironmentOutlined,
  EyeOutlined,
  FileImageOutlined,
  MoreOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useDownloadReceipt } from './DownloadReceiptButton';
import { VerMapaButton } from '../../../components/VerMapaButton';
import { useT } from '../../../locales';
import { coordenadasValidas } from '../../../lib/geo';
import type { PagoClienteResponse } from '@aid/shared';

export interface PagosTableActionsProps {
  pago: PagoClienteResponse;
  canAnular: boolean;
  onDetail: (id: number) => void;
  onAnular: (pago: PagoClienteResponse) => void;
  onViewBoleta: (id: number) => void;
}

export function PagosTableActions({
  pago,
  canAnular,
  onDetail,
  onAnular,
  onViewBoleta,
}: PagosTableActionsProps) {
  const t = useT();
  const activo = !pago.motivoAnulacion;
  const [mapOpen, setMapOpen] = useState(false);
  const receipt = useDownloadReceipt(pago);
  const ubicacion = coordenadasValidas(pago.latitud, pago.longitud);
  const tieneBoleta = pago.tieneBoleta === true;

  const menuItems: MenuProps['items'] = [
    {
      key: 'detail',
      label: t('common.ver'),
      icon: <EyeOutlined />,
    },
    ...(tieneBoleta
      ? [
          {
            key: 'boleta',
            label: t('pages.pagos.viewBoleta'),
            icon: <FileImageOutlined aria-hidden />,
          },
        ]
      : []),
    ...(activo
      ? [
          {
            key: 'receipt',
            label: receipt.label,
            icon: receipt.icon,
            disabled: receipt.descargando,
          },
        ]
      : []),
    ...(activo && canAnular
      ? [
          {
            key: 'annul',
            label: t('pages.pagos.anular'),
            icon: <StopOutlined />,
          },
        ]
      : []),
    ...(ubicacion
      ? [
          {
            key: 'map',
            label: t('components.verMapa'),
            icon: <EnvironmentOutlined />,
          },
        ]
      : []),
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'detail':
        onDetail(pago.id);
        break;
      case 'boleta':
        onViewBoleta(pago.id);
        break;
      case 'receipt':
        void receipt.descargar();
        break;
      case 'annul':
        onAnular(pago);
        break;
      case 'map':
        setMapOpen(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Dropdown trigger={['click']} menu={{ items: menuItems, onClick: handleMenuClick }}>
        <Button type="text" icon={<MoreOutlined aria-hidden />} aria-label={t('common.actions')} />
      </Dropdown>
      {ubicacion && (
        <VerMapaButton
          latitud={pago.latitud}
          longitud={pago.longitud}
          open={mapOpen}
          onOpenChange={setMapOpen}
          hiddenTrigger
        />
      )}
    </>
  );
}
